import React from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './page.component.module.styl';

import type {ComponentProps, ReactNode} from 'react';


const classNames = withCssModule(styles);

export type PageProps = {
    /**
     * Page chrome elements: `PageHeader`, `PageMain`, `PageFooter`, `PageNavbar`,
     * `PageLogo`, and `PageSearch`.
     */
    children: ReactNode,
    /**
     * Array of page-identifier strings. Each entry is appended as a `page_name-{name}`
     * modifier class on the root `<div>`, enabling page-specific CSS overrides from
     * a parent stylesheet without prop drilling.
     */
    pageName?: string[],
    /**
     * Additional CSS class merged onto the root `<div>`.
     */
    className?: string,
} & ComponentProps<'div'>;

/**
 * Root layout shell for a full application page.
 *
 * Renders a `<div>` with the `page` CSS class and appends a `page_name-{name}`
 * modifier class for each entry in `pageName`. This allows page-specific style
 * overrides from parent CSS without additional props drilling.
 *
 * Compose with `PageHeader`, `PageNavbar`, `PageMain`, `PageFooter`, and
 * `PageLogo` to build the full page chrome. Use `PageSearch` to add a search
 * bar in the header region.
 *
 * ## Accessibility
 * Use semantic landmark elements inside (e.g. `<header>`, `<main>`, `<nav>`,
 * `<footer>`) and follow the
 * [WAI-ARIA Landmark Regions](https://www.w3.org/WAI/ARIA/apg/practices/landmark-regions/)
 * guidelines.
 */
export function Page(props: PageProps) {
    const {
        pageName = [],
        children,
        className,
        ...restProps
    } = props;

    const classes = classNames('page', className, pageName.map(i => `page_name-${i}`));

    return (
        <div {...restProps} className={classes}>
            {children}
        </div>
    );
}
